import { useState } from 'react'

import { useListInvites } from 'src/features/invites/api/use-list-invites'
import { DownArrow } from 'src/shared/components/icons/down-arrow'
import { Button } from 'src/shared/components/ui/buttons/button'
import { ScrollArea, ScrollBar } from 'src/shared/components/ui/miscellaneous/scroll-area'
import { Dialog, DialogContent, DialogTrigger } from 'src/shared/components/ui/popups/dialog'
import type { Invite } from 'src/shared/schemas/invites/invite'

import { useGetCreditStatus } from '../api/use-get-credit-status'

import { AcceptedUser } from './accepted-user'
import { InviteItem } from './invite-item'
import InviteStatusIcon from './invite-status-icon'
import RewardsRecap from './reward-recap'
import { SendInviteForm } from './send-invite-form'
import AbsoluteCoin from './ui/absolute-coin'
import { UpdateInviteForm } from './update-invite-form'

type ContentView = 'lists' | 'invite-form' | 'phone-form'

interface SendInvitePopupProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  trigger: React.ReactNode
}

export const SendInvitePopup = ({ open, onOpenChange, trigger }: SendInvitePopupProps) => {
  const [view, setView] = useState<ContentView>('lists')
  const [selectedInvite, setSelectedInvite] = useState<Invite>()
  const { data } = useListInvites({ limit: 25 })
  const invites = data?.items || []
  const { data: creditStatus } = useGetCreditStatus()

  const earnedCredits = creditStatus?.earnedCredits || 0
  const invitesTowardNextCredit = creditStatus?.invitesTowardNextCredit || []
  const inviteCountTowardNextCredit = creditStatus?.invitesTowardNextCredit?.length || 0
  const pointsEarned = creditStatus?.pointsEarned || 0
  const invitesCompletedCount = creditStatus?.invitesCompletedCount || 0
  const invitesLeft = 5 - Math.max(inviteCountTowardNextCredit, 0)
  const inviteTypes: ('user' | 'invite' | 'reward')[] = [
    ...invitesTowardNextCredit.map((invite) => (invite.userId ? 'user' : 'invite')),
    ...Array(Math.max(invitesLeft - 1, 0)).fill('invite'),
    inviteCountTowardNextCredit === 5 ? 'user' : 'reward',
  ]

  const handlePhoneUpdate = (invite: Invite) => {
    setSelectedInvite(invite)
    setView('phone-form')
  }

  const handleSendInvite = () => {
    setView('invite-form')
  }

  const resetView = () => {
    setView('lists')
    setSelectedInvite(undefined)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-[900px] border-none bg-transparent p-0" hideClose>
        <div className="flex h-[600px] max-h-[80vh]">
          {/* Left side - Marketing content */}
          <div className="relative flex h-full w-1/2 flex-col items-center justify-between overflow-hidden rounded-l-2xl bg-primary p-5 pt-[100px] text-primary-foreground">
            <AbsoluteCoin className="left-[-120px] top-[-250px] -rotate-90" />
            <AbsoluteCoin className="right-[-220px] top-0" />
            <div className="flex flex-col items-center justify-center gap-5">
              <div className="flex flex-col items-center justify-center">
                <p className="text-center text-lg font-bold italic">Invite your network</p>
                <h2
                  className="text-center text-[28px] font-bold"
                  style={{
                    background: 'linear-gradient(270deg, #47F59E 3.83%, #04AFAD 94.76%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  Win qualified lead credit
                </h2>
              </div>
              <div className="flex flex-row items-center justify-center gap-2.5">
                {inviteTypes.map((type, idx) => (
                  <InviteStatusIcon
                    key={idx}
                    type={type}
                    invite={idx < invitesTowardNextCredit.length ? invitesTowardNextCredit[idx] : null}
                  />
                ))}
              </div>
              <div className="flex flex-row items-center gap-2">
                <p className="text-center text-base text-white/75">
                  <span className="font-bold text-white">{invitesLeft}</span> verified invite
                  {invitesLeft === 1 ? '' : 's'} left until <span className="font-bold text-white">reward</span>
                </p>
                <DownArrow size={16} className="text-white" style={{ transform: 'rotateX(180deg) rotateZ(-60deg)' }} />
              </div>
            </div>
            <div className="flex w-full flex-col items-center justify-center gap-5">
              <RewardsRecap
                totalAcceptedInvites={invitesCompletedCount}
                totalPoints={pointsEarned}
                totalQLC={earnedCredits}
              />
              <Button
                variant="secondaryInverse"
                size="xl"
                className="w-full gap-2 rounded-full text-[18px] font-bold"
                onClick={view === 'lists' ? handleSendInvite : resetView}
              >
                {view === 'lists' ? 'Send invite' : 'Go back'}
              </Button>
            </div>
          </div>

          {/* Right side - Dynamic content */}
          <div className="flex w-1/2 flex-col rounded-r-2xl bg-background">
            <ScrollArea className="h-[600px] max-h-[80vh] rounded-r-2xl" color="transparent">
              <div className="flex min-h-full flex-col bg-background p-5 px-10">
                {view === 'lists' && (
                  <div className="flex flex-col gap-10">
                    <section className="flex flex-col">
                      <div className="mb-4 flex flex-col items-start justify-between">
                        <div className="mb-2 flex flex-row items-center gap-2">
                          <h2 className="text-2xl font-bold">Invitation(s) sent</h2>
                          <DownArrow size={19} className="mt-4 text-muted" />
                        </div>
                        <p className="text-start text-sm text-muted">
                          Ensure your invites verify their profiles to earn a qualified lead credit!
                        </p>
                      </div>
                      {invites.length === 0 ? (
                        <div className="flex flex-col items-center justify-center">
                          <p className="text-center text-sm text-muted">No invitations sent yet</p>
                        </div>
                      ) : (
                        invites.map((invite) => {
                          if (invite.invitedUserId) {
                            return (
                              <AcceptedUser
                                key={invite.id}
                                invite={invite}
                                userId={invite.invitedUserId}
                                onOpenChange={onOpenChange}
                              />
                            )
                          }
                          return <InviteItem key={invite.id} invite={invite} onPhoneUpdate={handlePhoneUpdate} />
                        })
                      )}
                    </section>
                  </div>
                )}
                {view === 'invite-form' && (
                  <div className="flex min-h-full flex-1">
                    <SendInviteForm onSuccess={resetView} />
                  </div>
                )}
                {view === 'phone-form' && selectedInvite && (
                  <div className="flex min-h-full flex-1">
                    <UpdateInviteForm invite={selectedInvite} onSuccess={resetView} className="w-full" />
                  </div>
                )}
              </div>
              <ScrollBar orientation="vertical" className="mr-2" />
            </ScrollArea>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
